/**
 * A regua inteira: quem entrou, os toques em colunas e quem nao voltou.
 *
 * Layout em tres faixas so no desktop bem largo (`2xl`: entrada | toques |
 * saida) e empilhado abaixo disso. Os toques ficam em DUAS colunas a partir do
 * `md`, nunca quatro: com a lateral aberta, quatro colunas de copy inteira
 * dariam ~200px por cartao, e cortar a copy derrotaria o proposito do cartao.
 *
 * Abaixo de `md` a regua vira um CARROSSEL de um toque por vez, com
 * "Anterior / Proximo", indicador "Toque 2 de 4" anunciado por leitor de tela
 * e setas do teclado. Empilhar quatro cartoes de 1.200px cada num celular
 * obrigaria a rolar por metros para comparar T+1h com T+24h.
 *
 * A legenda (Pagou / Respondeu / Descadastrado) e texto com ponto colorido:
 * cor sozinha nunca comunica destino.
 */

import { useState } from 'react';
import { Badge, Button, Dot, Footnote, Panel, SectionCard } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatDate, formatRatio } from '../../../lib/format';
import { destaquesDaRegua } from '../agregacao';
import type { FunnelKind, OfferMetrics, TouchDefinition } from '../types';
import { useMediaQuery } from '../useMediaQuery';
import { VOCABULARIO, rotuloDoAtraso } from '../vocabulario';
import { ToqueCard } from './ToqueCard';

function LadoDaRegua({ tone, titulo, valor, apoio }: { readonly tone: 'accent' | 'critical'; readonly titulo: string; readonly valor: number; readonly apoio: string }): JSX.Element {
  return (
    <Panel tone={tone} className="flex flex-col items-center justify-center gap-1 px-4 py-5 text-center 2xl:sticky 2xl:top-24">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">{titulo}</p>
      <p className="text-3xl font-semibold tabular-nums tracking-tight text-ink">{formatCount(valor)}</p>
      <p className="text-xs text-ink-subtle">{apoio}</p>
    </Panel>
  );
}

export function ReguaDeToques({
  kind,
  touches,
  metrics,
  resultadosVisiveis,
  funnelSinceAt,
  feeBps,
}: {
  readonly kind: FunnelKind;
  readonly touches: readonly TouchDefinition[];
  readonly metrics: OfferMetrics;
  readonly resultadosVisiveis: boolean;
  readonly funnelSinceAt: string;
  readonly feeBps: number;
}): JSX.Element {
  const v = VOCABULARIO[kind];
  const largo = useMediaQuery('(min-width: 768px)');
  const [indiceBruto, setIndice] = useState(0);
  const indice = Math.min(indiceBruto, Math.max(0, touches.length - 1));

  const entraram = metrics.entered - metrics.returnedAlone;
  const ultimo = metrics.touches[metrics.touches.length - 1];
  const naoVoltou = ultimo?.endedWithoutReturn ?? 0;
  const naoMensuraveis = touches.filter((_, i) => metrics.touches[i]?.measurable === false);
  const destaques = destaquesDaRegua(metrics, touches);

  const disparados = metrics.touches.reduce((acc, t) => acc + t.reached, 0);
  const naFila = metrics.touches.reduce((acc, t) => acc + t.queued, 0);
  const voltaram = metrics.touches.reduce((acc, t) => acc + t.paid + t.closedBySeller, 0);
  const voltaramCents = metrics.touches.reduce((acc, t) => acc + t.paidAmountCents + t.closedBySellerAmountCents, 0);

  const toqueAtual = touches[indice];
  const metricaAtual = metrics.touches[indice];

  const irPara = (novo: number): void => {
    setIndice(Math.max(0, Math.min(touches.length - 1, novo)));
  };

  return (
    <SectionCard
      id="regua-de-toques"
      title={v.reguaTitulo}
      description={v.reguaSubtitulo}
      icon="disparo"
      aside={
        <Badge tone="neutral" icon="relogio">
          Começa {rotuloDoAtraso(kind, touches[0]?.delayMinutes ?? v.atrasoMinimoMinutos)} depois do {v.fato}
        </Badge>
      }
    >
      <div className="flex flex-col gap-4 2xl:grid 2xl:grid-cols-[9rem_minmax(0,1fr)_9rem] 2xl:items-start">
        <LadoDaRegua tone="accent" titulo={v.ladoEntradaTitulo} valor={entraram} apoio={v.ladoEntradaApoio} />

        {largo ? (
          <ol className="grid gap-4 md:grid-cols-2">
            {touches.map((toque, i) => {
              const m = metrics.touches[i];
              if (m === undefined) {
                return null;
              }
              return (
                <li key={toque.id} className="min-w-0">
                  <ToqueCard
                    kind={kind}
                    touch={toque}
                    metrics={m}
                    proximo={touches[i + 1] ?? null}
                    resultadosVisiveis={resultadosVisiveis}
                    funnelSinceAt={funnelSinceAt}
                    feeBps={feeBps}
                    destaques={destaques}
                  />
                </li>
              );
            })}
          </ol>
        ) : toqueAtual !== undefined && metricaAtual !== undefined ? (
          <div
            className="flex flex-col gap-3"
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') {
                e.preventDefault();
                irPara(indice + 1);
              } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                irPara(indice - 1);
              }
            }}
          >
            <div className="flex items-center justify-between gap-2">
              <Button variant="secondary" size="sm" icon="seta-esq" disabled={indice === 0} onClick={() => irPara(indice - 1)} aria-label="Toque anterior">
                Anterior
              </Button>
              <p role="status" aria-live="polite" className="text-center text-xs font-medium text-ink-muted">
                Toque {indice + 1} de {touches.length} · <span className="tabular-nums text-ink">{rotuloDoAtraso(kind, toqueAtual.delayMinutes)}</span>
              </p>
              <Button variant="secondary" size="sm" iconRight="seta-dir" disabled={indice >= touches.length - 1} onClick={() => irPara(indice + 1)} aria-label="Próximo toque">
                Próximo
              </Button>
            </div>
            <ol className="flex justify-center gap-2" aria-label="Ir direto a um toque">
              {touches.map((t, i) => (
                <li key={t.id}>
                  <button
                    type="button"
                    aria-current={i === indice ? 'true' : undefined}
                    aria-label={`Ir ao toque ${i + 1}, ${rotuloDoAtraso(kind, t.delayMinutes)}`}
                    onClick={() => irPara(i)}
                    className={`h-8 min-w-[2.75rem] rounded-full border px-2 text-[11px] font-semibold tabular-nums transition-colors ${
                      i === indice ? 'border-brand bg-brand text-white' : 'border-line bg-surface text-ink-muted hover:border-brand/50'
                    }`}
                  >
                    {rotuloDoAtraso(kind, t.delayMinutes)}
                  </button>
                </li>
              ))}
            </ol>
            <ToqueCard
              key={toqueAtual.id}
              kind={kind}
              touch={toqueAtual}
              metrics={metricaAtual}
              proximo={touches[indice + 1] ?? null}
              resultadosVisiveis={resultadosVisiveis}
              funnelSinceAt={funnelSinceAt}
              feeBps={feeBps}
              destaques={destaques}
            />
          </div>
        ) : null}

        <LadoDaRegua tone="critical" titulo={v.ladoSaidaTitulo} valor={naoVoltou} apoio={v.ladoSaidaApoio} />
      </div>

      <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-ink-muted" aria-label="Legenda dos destinos">
        <li className="flex items-center gap-2">
          <Dot tone="good" />
          <span>
            <strong className="text-ink">Pagou</strong> — sai e não recebe o resto
          </span>
        </li>
        <li className="flex items-center gap-2">
          <Dot tone="violet" />
          <span>
            <strong className="text-ink">Respondeu</strong> (botão ou digitando) — sai e a vendedora assume
          </span>
        </li>
        <li className="flex items-center gap-2">
          <Dot tone="neutral" />
          <span>
            <strong className="text-ink">Descadastrado</strong> — nem entra
          </span>
        </li>
        {destaques.melhor !== null ? (
          <li className="flex items-center gap-2">
            <Dot tone="good" />
            <span>
              <strong className="text-ink">Melhor / menor conversão</strong> — quem voltou dividido por quem recebeu, só entre toques ligados com 10+ pessoas
            </span>
          </li>
        ) : null}
      </ul>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-1 border-t border-line pt-3 text-sm text-ink-muted">
        <span>
          <strong className="tabular-nums text-ink">{formatCount(disparados)}</strong> {kind === 'expirados' ? 'disparados' : 'toques'} no período
        </span>
        <span>
          <strong className="tabular-nums text-ink">{formatCount(naFila)}</strong> na fila hoje
        </span>
        <span>
          <strong className="tabular-nums text-status-good">{formatCount(voltaram)}</strong> {kind === 'expirados' ? 'voltaram' : 'recuperados'} pela régua · {formatRatio(voltaram, entraram)} ·{' '}
          {formatBRLFromCents(voltaramCents)}
        </span>
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        {v.notasDaRegua.map((nota) => (
          <Footnote key={nota}>{nota}</Footnote>
        ))}
        <Footnote>{v.notaDepoisDoFato}</Footnote>
        {naoMensuraveis.length > 0 ? (
          <Footnote>
            {naoMensuraveis.map((t) => rotuloDoAtraso(kind, t.delayMinutes)).join(', ')} ainda não {naoMensuraveis.length === 1 ? 'tem' : 'têm'} como ser {naoMensuraveis.length === 1 ? 'medido' : 'medidos'}:
            o funil começou em {formatDate(funnelSinceAt)} e quem venceu depois disso não teve tempo de chegar lá. Vai aparecendo com o passar dos dias.
          </Footnote>
        ) : null}
      </div>
    </SectionCard>
  );
}
